import React, { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory, useParams } from 'react-router-dom'
import { makeStyles } from '@material-ui/core/styles'
import { useSnackbar } from 'notistack'
import { track } from '@amplitude/analytics-browser'
import { SettingsLayout } from 'src/components/app-ui'
import {
  getCourseByIdAction,
  getCourseLandingPageAction,
} from 'src/reducers/course/action'
import {
  messageBoxShowAction,
  messageBoxHideAction,
} from 'src/reducers/message-box/action'
import MessageBoxContent, {
  MessageBoxType,
  MessageBoxVariant,
} from 'src/model/message-box'
import { ButtonData, ButtonType, ButtonActionType } from 'src/model/button'
import { useCoursePublish } from 'src/contexts/course-publish-context'
import { coursePath } from '../tabs'
import useCourseLinks from './use-course-links'
import LinksView from './links-view'
import {
  AccessNote,
  EmbedView,
  IframeView,
  LinkNote,
  PdfView,
  ScormView,
} from './delivery-views'

/** `value` is the `share/:subPage` URL segment. */
const SECTIONS = [
  { value: 'links', label: 'Links', icon: 'tabler:link' },
  {
    value: 'embed',
    label: 'Embed',
    icon: 'tabler:code',
    trailingIcon: 'tabler:link',
    trailingHint: 'Uses the direct link',
  },
  {
    value: 'iframe',
    label: 'iFrame',
    icon: 'tabler:devices',
    trailingIcon: 'tabler:link',
    trailingHint: 'Uses the direct link',
  },
  {
    value: 'scorm',
    label: 'SCORM',
    icon: 'tabler:cube',
    trailingIcon: 'tabler:link',
    trailingHint: 'Uses the direct link',
  },
  {
    value: 'pdf',
    label: 'PDF',
    icon: 'tabler:file-type-pdf',
  },
]

const useStyles = makeStyles({
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    minWidth: 0,
    maxWidth: 760,
  },
})

/** Asks before moving a published course back to draft. */
const useMoveToDraft = () => {
  const dispatch = useDispatch()
  const publishState = useCoursePublish()
  const { enqueueSnackbar } = useSnackbar()

  return () =>
    dispatch(
      messageBoxShowAction(
        MessageBoxContent({
          type: MessageBoxType.WARNING,
          buttons: [
            new ButtonData(
              ButtonType.NORMAL,
              'Cancel',
              ButtonActionType.NO,
              () => dispatch(messageBoxHideAction()),
            ),
            new ButtonData(
              ButtonType.ACTION,
              'Move to draft',
              ButtonActionType.DELETE,
              async () => {
                try {
                  await publishState.unpublishCourse()
                  track('course_unpublished')
                  enqueueSnackbar(
                    'Moved to draft. Learners no longer see it.',
                    {
                      variant: 'success',
                    },
                  )
                } catch (error) {
                  enqueueSnackbar('Could not move this course to draft.', {
                    variant: 'error',
                  })
                } finally {
                  dispatch(messageBoxHideAction())
                }
              },
            ),
          ],
          title: 'Move this course back to draft?',
          subTitle: null,
          description:
            'Learners lose access right away and the share link stops working. Content and learner progress are kept, so you can publish it again later.',
          maxWidth: '500px',
          variant: MessageBoxVariant.NOTICE,
        }),
      ),
    )
}

const ShareTab = ({ course }) => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const history = useHistory()
  const { id, subPage } = useParams()
  const me = useSelector(state => state.authentication.me)
  const publishState = useCoursePublish()
  const moveToDraft = useMoveToDraft()

  useEffect(() => {
    dispatch(getCourseByIdAction(course.id, () => {}))
    dispatch(getCourseLandingPageAction(course.id))
  }, [dispatch, course.id])

  const active =
    SECTIONS.find(section => section.value === subPage) || SECTIONS[0]

  const links = useCourseLinks(course)
  const open = value => history.push(coursePath(id, 'share', value))

  const views = {
    embed: <EmbedView course={course} />,
    iframe: <IframeView directUrl={links.directUrl} />,
    scorm: <ScormView course={course} me={me} />,
    pdf: <PdfView me={me} />,
  }

  return (
    <SettingsLayout
      items={SECTIONS}
      value={active.value}
      onChange={open}
      navWidth={220}
      minHeight={480}
    >
      <div className={classes.content}>
        {active.value === 'links' ? (
          <LinksView
            course={course}
            links={links}
            publishState={publishState}
            onMoveToDraft={moveToDraft}
          />
        ) : (
          <>
            <LinkNote view={active.value} directUrl={links.directUrl} />
            {views[active.value]}
            <AccessNote course={course} />
          </>
        )}
      </div>
    </SettingsLayout>
  )
}

export default ShareTab
